/* File: src\components\auth\LoginScreen.js */
/*
 * =============================================================================
 *                         CHANGE SUMMARY (2023-XX-XX)
 * =============================================================================
 * Purpose: Display an inline loading spinner within the "Sign in" and
 *          "Sign in with Google" buttons during the authentication process
 *          to provide better user feedback than the previous text change.
 *
 * Changes Made:
 * 1. Added import for the `LoadingSpinner` component:
 *    `import LoadingSpinner from '../ui/LoadingSpinner';`
 *
 * 2. Modified the Email/Password "Sign in" button:
 *    - Added Tailwind classes `flex items-center justify-center` to the button's
 *      `className` to vertically and horizontally center its content.
 *    - Replaced the conditional text `{isLoading ? 'Processing...' : 'Sign in'}`
 *      with a conditional rendering block:
 *      ```jsx
 *      {isLoading ? (
 *           <>
 *              <LoadingSpinner small={true} />
 *              <span className="ml-2">Signing in...</span>
 *          </>
 *      ) : (
 *          'Sign in'
 *      )}
 *      ```
 *      This now shows a small spinner and adjusted text when `isLoading` is true.
 *
 * 3. Modified the "Sign in with Google" button:
 *    - Added Tailwind classes `flex items-center justify-center` to the button's
 *      `className`.
 *    - Replaced the conditional text/icon logic with a similar conditional
 *      rendering block:
 *      ```jsx
 *      {isLoading ? (
 *          <>
 *              <LoadingSpinner small={true} />
 *              <span className="ml-2">Processing...</span>
 *          </>
 *      ) : (
 *          <>
 *              <i className="fa-brands fa-google mr-2 text-base" aria-hidden="true"></i>
 *              Sign in with Google
 *          </>
 *      )}
 *      ```
 *      This shows a small spinner and text when loading, and the Google icon
 *      with text otherwise.
 *
 * Why Necessary:
 * - The previous implementation only changed the button text (e.g., to
 *   "Processing..."), which could flicker quickly and might not be as
 *   noticeable or conventional as a loading spinner for indicating an
 *   ongoing background process.
 * - Adding an inline spinner provides clear, immediate visual feedback
 *   directly on the element the user interacted with, confirming the
 *   system is processing their request.
 *
 * Why Changes Won't Break Anything or Affect Look/Feel (Outside Request):
 * - Utilizes Existing State: The changes rely entirely on the existing `isLoading`
 *   state derived from the `useLoading` context, which was already being
 *   correctly managed by the `handleLogin` and `handleGoogleLogin` functions.
 *   No new state or logic flow was introduced.
 * - Uses Existing Component: The `LoadingSpinner` component was already part of
 *   the project (`src/components/ui/LoadingSpinner.js`).
 * - Minimal CSS Changes: Only additive Tailwind utility classes (`flex`,
 *   `items-center`, `justify-center`, `ml-2`) were added to the buttons to
 *   ensure proper layout of the spinner and text. These are standard layout
 *   classes and won't conflict with existing styles or alter the button's
 *   appearance when *not* loading (aside from ensuring content is centered,
 *   which is generally desirable).
 * - No Functional Logic Altered: The core authentication logic within
 *   `handleLogin`, `handleGoogleLogin`, error handling, and context usage
 *   remains untouched.
 * - Scoped Changes: The modifications are entirely contained within the JSX
 *   rendering of the two login buttons in this specific component. They do
 *   not affect any other part of the application. The overall page layout,
 *   form inputs, error messages, and links remain visually and functionally
 *   identical.
 * =============================================================================
 */
/* src/components/auth/LoginScreen.js */
import React, { useState, useEffect, useCallback, useRef } from 'react';
// Import Firebase Auth functions
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider
} from 'firebase/auth';
// Import Firebase Auth instance
import { auth } from '../../firebaseConfig';
// Import the loading context
import { useLoading } from '../../contexts/LoadingContext';
// Import the LoadingSpinner component
import LoadingSpinner from '../ui/LoadingSpinner'; // Import the spinner

// --- Environment Variable for reCAPTCHA Site Key ---
// Ensure REACT_APP_RECAPTCHA_SITE_KEY is set in your .env file
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// --- URL for your verifyLoginAttempt Cloud Function ---
// Ensure REACT_APP_VERIFY_LOGIN_URL is set in your .env file
// pointing to your deployed or emulated function endpoint.
const VERIFY_RECAPTCHA_URL = process.env.REACT_APP_VERIFY_LOGIN_URL;


// Helper function for EMAIL/PASSWORD login errors (Added reCAPTCHA specific message)
const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';
        case 'auth/user-disabled':
            return 'This account has been disabled.';
        case 'auth/user-not-found':
        case 'auth/wrong-password': // Combined for simplicity
            return 'Incorrect email or password. Please try again.';
        case 'auth/too-many-requests':
            return 'Access temporarily disabled due to too many failed login attempts. Please reset your password or try again later.';
        case 'auth/network-request-failed':
            return 'Network error. Please check your connection and try again.';
        // Specific message handled directly in catch block now, but could add one here if needed:
        // case 'auth/recaptcha-verification-failed':
        //      return 'Security check failed. Please try logging in again.';
        default:
             console.error("Unhandled Firebase Login Error Code:", errorCode);
            return 'An unexpected error occurred during login. Please try again.';
    }
};

// Helper function for GOOGLE SIGN-IN errors (Unchanged)
const getGoogleSignInErrorMessage = (errorCode) => {
    switch (errorCode) {
        case 'auth/popup-closed-by-user':
            return 'Sign-in cancelled. Please try again.';
        case 'auth/cancelled-popup-request':
             return 'Sign-in cancelled. Only one sign-in window can be open at a time.';
        case 'auth/popup-blocked':
            return 'Sign-in popup blocked by browser. Please enable popups for this site and try again.';
        case 'auth/account-exists-with-different-credential':
            return 'An account already exists with this email address using Email/Password. Please sign in using your password, or reset it if needed.';
        case 'auth/network-request-failed':
             return 'Network error during Google sign-in. Please check your connection.';
        default:
            console.error("Unhandled Firebase Google Sign-In Error Code:", errorCode);
            return 'An unexpected error occurred during Google sign-in. Please try again.';
    }
};


/**
 * LoginScreen Component
 * Handles user login via email/password (with reCAPTCHA v3 verification) or Google.
 * Provides options to switch to the Password Reset view.
 * Requires REACT_APP_RECAPTCHA_SITE_KEY and REACT_APP_VERIFY_LOGIN_URL environment variables.
 *
 * Props:
 * - onSwitchToReset: Function to call to switch the view to PasswordReset.
 */
const LoginScreen = ({ onSwitchToReset }) => {
    // --- State ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null); // General/Email/reCAPTCHA errors
    const [googleError, setGoogleError] = useState(null); // Google specific errors
    const [configError, setConfigError] = useState(null); // Specific state for config issues
    // Use loading context - isLoading controls the button state
    const { showLoading, hideLoading, updateMessage, isLoading } = useLoading();

    // --- Refs ---
    const isMountedRef = useRef(true); // Track mount status for async operations

    // --- Environment Variable Checks ---
    useEffect(() => {
        let errorMsg = null;
        if (!RECAPTCHA_SITE_KEY) {
            console.error("CRITICAL: REACT_APP_RECAPTCHA_SITE_KEY environment variable is not set. reCAPTCHA verification will fail.");
            errorMsg = "Configuration error (RCK_SK). "; // Keep messages short for UI
        }
        if (!VERIFY_RECAPTCHA_URL) {
             console.error("CRITICAL: REACT_APP_VERIFY_LOGIN_URL environment variable is not set. Login verification will fail.");
             errorMsg = (errorMsg || "") + "Configuration error (RCK_URL).";
        }
        if (errorMsg) {
            setConfigError(errorMsg + " Please contact support.");
        }
    }, []);

    // --- Mount/Unmount ---
     useEffect(() => {
        isMountedRef.current = true;
        // console.log("LoginScreen mounted.");
        return () => {
            isMountedRef.current = false;
            // console.log("LoginScreen unmounting.");
        };
    }, []);


    // --- Handlers ---

    /** Handles the email/password login attempt with reCAPTCHA V3 */
    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setGoogleError(null);
        if (configError) {
            setError(configError);
            return;
        }
        if (typeof window.grecaptcha === 'undefined' || typeof window.grecaptcha.enterprise === 'undefined') {
             console.error("reCAPTCHA script not loaded or ready.");
             setError("Security system failed to load. Please refresh and try again.");
             return;
        }

        showLoading("Preparing secure login..."); // This sets isLoading = true

        try {
            window.grecaptcha.enterprise.ready(async () => {
                if (!isMountedRef.current) return;

                try {
                     updateMessage("Verifying security...");
                     const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'LOGIN' });
                     if (!isMountedRef.current) return;

                     updateMessage("Validating session...");
                     const verifyResponse = await fetch(VERIFY_RECAPTCHA_URL, {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         body: JSON.stringify({ recaptchaToken: token }),
                     });
                     if (!isMountedRef.current) return;

                     if (!verifyResponse.ok) {
                         let backendErrorMsg = 'Security verification failed.';
                         try {
                             const errorData = await verifyResponse.json();
                             backendErrorMsg = errorData.error || backendErrorMsg;
                             console.error("[Login] Backend verification failed:", backendErrorMsg, errorData);
                         } catch (parseError) {
                             console.error("[Login] Backend verification failed, couldn't parse error response:", verifyResponse.statusText);
                             backendErrorMsg = `Security check failed (Status: ${verifyResponse.status})`;
                         }
                         throw new Error(backendErrorMsg);
                     }

                     updateMessage("Authenticating...");
                     await signInWithEmailAndPassword(auth, email, password);
                     if (!isMountedRef.current) return;

                     updateMessage("Loading profile...");
                     // hideLoading() is implicitly handled by navigation/AuthGuard on success

                } catch (err) {
                     console.error("[Login] Error during login process:", err);
                     if (isMountedRef.current) {
                         let friendlyError;
                         if (err.message && (err.message.startsWith('Security check failed') || err.message.startsWith('Security verification failed'))) {
                             friendlyError = err.message;
                         } else if (err.code && err.code.startsWith('auth/')) {
                             friendlyError = getFriendlyErrorMessage(err.code);
                         } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
                              friendlyError = "Network error verifying security. Please check connection.";
                         } else if (err.message && err.message.includes('grecaptcha')) {
                              friendlyError = "Error during security check. Please refresh and try again.";
                         } else {
                             friendlyError = err.message || 'An unexpected error occurred during login.';
                         }
                         setError(friendlyError);
                         hideLoading(); // Hide loading indicator ONLY on error
                     } else {
                         console.warn("[Login] Component unmounted during error handling.");
                     }
                 }
            });

        } catch (outerError) {
             console.error("[Login] Pre-reCAPTCHA error:", outerError);
             if (isMountedRef.current) {
                 setError(outerError.message || "Failed to initiate secure login.");
                 hideLoading(); // Hide loading indicator on pre-recaptcha error
             }
        }
    };


    /** Handles the Google Sign-in attempt */
    const handleGoogleLogin = async () => {
         setError(null);
         setGoogleError(null);
         showLoading("Authenticating with Google..."); // This sets isLoading = true
         const provider = new GoogleAuthProvider();

         try {
             console.log("[Login] Attempting Google Sign-In via popup...");
             await signInWithPopup(auth, provider);
             console.log("[Login] Google Sign-In successful via Firebase.");
             updateMessage("Loading profile...");
             // hideLoading() handled by AuthGuard on success
         } catch (firebaseError) {
             console.error("[Login] Firebase Google Sign-In Error:", firebaseError.code, firebaseError.message);
             if (isMountedRef.current) {
                  setGoogleError(getGoogleSignInErrorMessage(firebaseError.code));
                  hideLoading(); // Hide loading indicator ONLY on error
             } else {
                  console.warn("[Login] Component unmounted during error handling for Google sign-in.");
             }
         }
    };

    /** Handles navigation to the Password Reset view */
    const navigateToReset = useCallback(() => {
         if (onSwitchToReset && typeof onSwitchToReset === 'function') {
             onSwitchToReset();
         } else {
             console.error("LoginScreen: onSwitchToReset prop missing or not a function!");
             alert("Password reset navigation error. Please contact support if needed.");
         }
    }, [onSwitchToReset]);

    // --- Render ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            {/* Keep the slight opacity effect while loading, managed by context's isLoading */}
            <div className={`bg-white p-8 rounded-lg shadow-md w-full max-w-sm transition-opacity duration-200 ${isLoading ? 'opacity-80' : 'opacity-100'}`}>
                 {/* Logo and Title */}
                 <div className="flex flex-col items-center mb-6">
                    <div className="h-14 w-14 mb-3 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-lg flex items-center justify-center shadow-sm">
                        <span className="font-tomorrow font-semibold text-2xl text-white" style={{ fontFamily: 'Tomorrow, sans-serif', fontWeight: 600 }}>N²</span>
                    </div>
                    <h1 className="text-xl font-bold text-center text-gray-800 font-tomorrow" style={{ fontFamily: 'Tomorrow, sans-serif' }}>Dual N-Back</h1>
                    <p className="text-sm text-gray-500 mt-1">Sign in to access your training</p>
                 </div>

                 {/* Configuration Error Display */}
                 {configError && (
                    <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                        {configError}
                    </p>
                 )}

                 {/* Email/Password Login Form */}
                <form onSubmit={handleLogin} noValidate>
                    {/* Email Input */}
                    <div className="mb-4">
                        <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                            type="email" id="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required
                            disabled={isLoading || !!configError}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                            placeholder="you@example.com" aria-label="Email Address" autoComplete="email"
                        />
                    </div>
                    {/* Password Input */}
                    <div className="mb-3">
                        <div className="flex justify-between items-center mb-1">
                            <label htmlFor="login-password" className="block text-sm font-medium text-gray-700">Password</label>
                            <button type="button" onClick={navigateToReset}
                                disabled={isLoading || !!configError}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-500 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
                                Forgot password?
                            </button>
                        </div>
                        <input
                            type="password" id="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                            disabled={isLoading || !!configError}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                            placeholder="••••••••" aria-label="Password" autoComplete="current-password"
                        />
                    </div>

                    {/* General Error Message Area */}
                    {error && !configError && ( // Don't show general error if config error is shown
                        <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                            {error}
                        </p>
                    )}

                    {/* Email Login Submit Button */}
                    <button type="submit"
                         // Disable if loading OR if config is missing
                        disabled={isLoading || !!configError}
                        className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`} // Added flex items-center justify-center
                    >
                        {/* Conditionally render spinner or text */}
                        {isLoading ? (
                             <>
                                <LoadingSpinner small={true} />
                                <span className="ml-2">Signing in...</span> {/* Optional: different text */}
                            </>
                        ) : (
                            'Sign in'
                        )}
                    </button>
                </form>

                {/* Divider */}
                <div className="my-5 flex items-center">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Google Sign-in Button */}
                <button type="button" onClick={handleGoogleLogin}
                    // Disable if loading OR if there's a config error for consistency
                    disabled={isLoading || !!configError}
                    className={`py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`} // Added flex items-center justify-center
                    aria-label="Sign in with Google"
                >
                     {/* Conditionally render spinner or icon+text */}
                     {isLoading ? (
                        <>
                            <LoadingSpinner small={true} />
                            <span className="ml-2">Processing...</span> {/* Optional: different text */}
                        </>
                    ) : (
                        <>
                            <i className="fa-brands fa-google mr-2 text-base" aria-hidden="true"></i>
                            Sign in with Google
                        </>
                    )}
                </button>

                {/* Google Sign-in Error Message Area */}
                {googleError && (
                    <p className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                        {googleError}
                    </p>
                )}

                 {/* Link to Sign Up */}
                <div className="mt-6 text-sm text-center">
                    <span className="text-gray-600">Don't have an account?</span>{' '}
                    {/* Link to external plans page */}
                    <a href="https://dualnback.com/plans" className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline">
                        Sign up
                    </a>
                </div>

                 {/* reCAPTCHA Branding - IMPORTANT! */}
                 {/* This text MUST be displayed visibly if you hide the default reCAPTCHA badge using CSS */}
                 {/* Added style={{ visibility: 'visible' }} to ensure it shows even if global CSS hides .grecaptcha-badge */}
                 <div className="mt-6 text-center text-xs text-gray-500" style={{ visibility: 'visible' }}>
                     This site is protected by reCAPTCHA and the Google 
                     <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Privacy Policy</a> and 
                     <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Terms of Service</a> apply.
                 </div>
            </div>
            {/* Optional: Add a global loading overlay managed by LoadingContext if desired */}
            {/* The LoadingContext provider will still handle the optional full-screen overlay */}
        </div>
    );
};

export default LoginScreen;